import { useCallback, useEffect, useMemo, useState } from "react";
import { useScreenDesignContext } from "./ScreenDesignProvider";

export enum screenType {
  "mobile" = "sm",
  "smallPc" = "lg",
  "middlePc" = "xl",
  "bigPc" = "xxl",
}
const condition: Record<screenType, { min: number; max: number }> = {
  [screenType.mobile]: { min: 0, max: 621 },
  [screenType.smallPc]: { min: 768, max: 992 },
  [screenType.middlePc]: { min: 992, max: 1200 },
  [screenType.bigPc]: { min: 1200, max: 1600 },
};
type SymbolStr = ">=" | "<=";

export const FORCE_MOBILE_SHELL_WIDTH = 360;

export const getScreenDesignMatch = (
  width: number,
  type: screenType = screenType.mobile,
  symbolStr?: SymbolStr,
  options?: { forceMobile?: boolean }
) => {
  const viewportWidth = options?.forceMobile ? FORCE_MOBILE_SHELL_WIDTH : width;
  const baseCondition = condition[type] || condition[screenType.mobile];
  const curCondition = { ...baseCondition };

  if (symbolStr === "<=") {
    curCondition.min = 0;
  } else if (symbolStr === ">=") {
    curCondition.max = 3600;
  }

  return viewportWidth > curCondition.min && viewportWidth < curCondition.max;
};

/**
 * 使用方法：
 * import  useScreenDesign,{screenType} from "@/Core/utils/hooks/useScreenDesign";
 *
 * const isMobile = useScreenDesign();
 */
const useScreenDesign = (type?: screenType, symbolStr?: SymbolStr, cb?: any) => {
  const screenDesignType = type || screenType.mobile;
  const { forceMobile } = useScreenDesignContext();
  const isMobileDevice = useMemo(
    () =>
      navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i) !=
      null,
    []
  );
  const getMatch = useCallback(
    (width: number) =>
      getScreenDesignMatch(width, screenDesignType, symbolStr, { forceMobile }),
    [forceMobile, screenDesignType, symbolStr]
  );
  const initStatus = isMobileDevice ? isMobileDevice : getMatch(window.innerWidth);
  const [isScreen, setIsScreen] = useState(initStatus);

  useEffect(() => {
    function handleResize() {
      const nextIsScreen = getMatch(window.innerWidth);
      cb && cb(nextIsScreen);
      setIsScreen(nextIsScreen);
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [cb, getMatch]);

  return isMobileDevice ? isMobileDevice : isScreen;
};

export default useScreenDesign;
